import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import ContentModeration from '../components/ContentModeration';
import VoiceRoomList from '../components/VoiceRoomList';
import { userAPI, streamAPI, getImageUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import ReportModal from '../components/ReportModal';
import { PageHeader } from '../components/PageHeader';
import { Row, Col } from '../components/Layout';
import { StreamCard } from '../components/StreamCard';

interface Stream {
  id: string;
  streamer: string;
  title: string;
  viewers: number;
  thumbnail: string;
  category: string;
  isLive: boolean;
  startTime: string;
  age: number;
  isPopular: boolean;
  isBeautiful: boolean;
  tags: string[];
  isSuspended?: boolean;
  streamerId: string;
  isVerified?: boolean;
  streamKey?: string;
}

interface DiscoverPageProps {
  initialRoomData?: { title: string; category: string; type: 'voice' | 'video' } | null;
  onRoomCreated?: () => void;
  onNavigate?: (page: string) => void;
  onWatchStream?: (streamId: string) => void;
  onViewProfile?: (userId: string) => void;
  onDrawer?: () => void;
}

const CATEGORIES = ["Tümü", "Sohbet", "Oyun", "Eğlence", "Müzik", "Güzellik"];
const TRENDS = ["#yazgeceleri", "#komedi", "#canlımüzik", "#pubg", "#sohbet"];

const DiscoverPage: React.FC<DiscoverPageProps> = ({ initialRoomData, onRoomCreated, onNavigate, onWatchStream, onViewProfile, onDrawer }) => {
  const { t } = useTranslation();
  const [streams, setStreams] = useState<Stream[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cat, setCat] = useState("Tümü");
  const { user, isVipActive } = useAuth();

  // Real logic kept intact
  useEffect(() => {
    const fetchStreams = async () => {
      try {
        const activeStreams = await streamAPI.getStreams();
        const formattedStreams: Stream[] = activeStreams.map((s: any) => ({
          id: s._id,
          streamer: s.streamer.username,
          title: s.title,
          viewers: s.viewers,
          thumbnail: s.streamer.profileImage || 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400',
          category: 'just-chatting',
          isLive: s.isActive,
          startTime: s.startTime,
          age: 20,
          isPopular: s.viewers > 100,
          isBeautiful: false,
          tags: ['canlı'],
          streamerId: s.streamer._id,
          isVerified: false,
          streamKey: s.streamKey
        }));
        setStreams(formattedStreams);
      } catch (err) {
        console.error("Failed to fetch streams:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStreams();

    import('../services/socket').then(({ socketService }) => {
      const socket = socketService.getSocket();
      if (!socket) return;
      socket.on('streamStarted', (newStreamData) => {
        setStreams(prev => [{
          id: newStreamData.streamKey,
          streamer: newStreamData.streamer,
          title: newStreamData.title,
          viewers: 0,
          thumbnail: newStreamData.streamerProfileImage || 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400',
          category: 'just-chatting',
          isLive: true,
          startTime: new Date().toISOString(),
          age: 20,
          isPopular: false,
          isBeautiful: false,
          tags: ['canlı'],
          streamerId: newStreamData.streamerId,
          isVerified: false,
          streamKey: newStreamData.streamKey
        }, ...prev]);
      });
      socket.on('streamStopped', (stoppedData) => {
        setStreams(prev => prev.filter(s => s.streamer !== stoppedData.streamer));
      });
    });
  }, []);

  // Fetch users when searching
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      userAPI.getUsers(searchQuery).then(res => setUsers(res)).catch(() => {});
    } else {
      setUsers([]);
    }
  }, [searchQuery]);

  const filtered = streams.filter(s =>
    (cat === "Tümü" || s.category === cat) &&
    (s.title.toLowerCase().includes(searchQuery.toLowerCase()) || s.streamer.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', animation: 'slideL .4s ease' }}>
      <PageHeader title="Keşfet" onDrawer={onDrawer} />

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 85 }}>

        {/* Search */}
        <div style={{ padding: '0 18px 16px' }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16,
            display: 'flex', alignItems: 'center', padding: '0 16px', height: 48, transition: 'border .2s'
          }}>
            <span style={{ fontSize: 16, color: 'var(--muted)', marginRight: 10 }}>🔍</span>
            <input
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Yayıncı, konu veya etiket ara..."
              style={{ flex: 1, background: 'none', border: 'none', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'DM Sans' }}
            />
          </div>
        </div>

        {/* Categories */}
        {!searchQuery && (
          <div style={{ padding: '0 0 16px' }}>
            <div style={{ display: 'flex', gap: 7, padding: '0 18px', overflowX: 'auto', scrollbarWidth: 'none' }}>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCat(c)}
                  style={{
                    whiteSpace: 'nowrap', flexShrink: 0,
                    background: c === cat ? 'linear-gradient(135deg,rgba(0,229,204,.15),rgba(59,130,246,.15))' : 'var(--surface2)',
                    border: `1px solid ${c === cat ? 'rgba(0,229,204,.3)' : 'var(--border)'}`,
                    borderRadius: 10, fontSize: 12, color: c === cat ? 'var(--cyan)' : 'var(--muted)',
                    padding: '7px 14px', cursor: 'pointer', transition: 'all .2s'
                  }}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Voice Rooms */}
        {!searchQuery && (
          <div style={{ padding: '16px 18px' }}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="syne" style={{ fontSize: 16, fontWeight: 700 }}>Ses Odaları 🎙️</div>
              <button style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 11, cursor: 'pointer' }}>Tümünü Gör</button>
            </Row>
            <VoiceRoomList onRoomCreated={onRoomCreated} initialRoomData={initialRoomData} />
          </div>
        )}

        {/* Trending Tags */}
        {!searchQuery && (
          <div style={{ padding: '0 18px 24px' }}>
            <div className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Trend Etiketler 🔥</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TRENDS.map((t, i) => (
                <div key={i} onClick={() => setSearchQuery(t.replace('#', ''))} style={{
                  background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '6px 12px', fontSize: 11, color: 'var(--text)', cursor: 'pointer'
                }}>
                  <span style={{ color: 'var(--coral)', marginRight: 2 }}>#</span>{t.replace('#', '')}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Search Results */}
        {searchQuery && users.length > 0 && (
          <div style={{ padding: '0 18px 24px' }}>
            <div className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Kullanıcılar</div>
            <div style={{ display: 'flex', gap: 15, overflowX: 'auto', paddingBottom: 10, scrollbarWidth: 'none' }}>
              {users.map((u: any) => {
                const uid = u._id || u.id;
                return (
                <div key={uid} onClick={() => onViewProfile && onViewProfile(uid)} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  cursor: 'pointer', flexShrink: 0, width: 70
                }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: '50%',
                    background: 'var(--surface2)', overflow: 'hidden', border: '2px solid rgba(255,92,92,.3)'
                  }}>
                    {u.profileImage ? (
                      <img src={getImageUrl(u.profileImage)} alt={u.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🥷</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
                    @{u.username}
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>Yayınlar yükleniyor...</div>
        ) : (
          <div style={{ padding: '0 18px' }}>
            <div className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Şu An Popüler</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {filtered.map(s => (
                <StreamCard
                  key={s.id}
                  stream={{
                    ...s,
                    name: s.streamer,
                    topic: s.title,
                    avatar: s.thumbnail.startsWith('http') ? <img src={s.thumbnail} width="100%" height="100%" /> : s.thumbnail,
                    accent: '#00E5CC',
                    thumb: '#0d1f2d',
                    audio: false
                  }}
                  onClick={() => onWatchStream && onWatchStream(s.streamKey || s.id)}
                />
              ))}
            </div>
            {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Gösterilecek yayın bulunamadı.</div>}
          </div>
        )}
      </div>
    </div>
  );
};
export default DiscoverPage;
